import React, { useState, useMemo } from 'react';
import {
  Card,
  Statistic,
  Typography,
  Space,
  Button,
  Select,
  Tag,
  Progress,
  Badge,
  Modal,
  List,
  Row,
  Col,
  Spin,
  Alert
} from 'antd';
import {
  MedicineBoxOutlined,
  ShoppingCartOutlined,
  DollarOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  ReloadOutlined,
  FilterOutlined,
  EyeOutlined,
  BarChartOutlined
} from '@ant-design/icons';
import { useQuery } from 'react-query';
import api from '../../services/api';
import dayjs, { Dayjs } from 'dayjs';
import { DatePickerModal } from '../../components/common/DatePickerModal';
import { useResponsive } from '../../hooks/useResponsive';
import styles from './DashboardPageMobile.module.css';

const { Title, Text } = Typography;
const { Option } = Select;

const DashboardPageMobile: React.FC = () => {
  const { isMobile, isTablet, isDesktop, screenWidth } = useResponsive();
  
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().subtract(30, 'days'),
    dayjs()
  ]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [lowStockVisible, setLowStockVisible] = useState(false);
  const [expiringVisible, setExpiringVisible] = useState(false);
  const [filterExpanded, setFilterExpanded] = useState(false);

  // Date picker modal states
  const [startDateModalOpen, setStartDateModalOpen] = useState(false);
  const [endDateModalOpen, setEndDateModalOpen] = useState(false);
  const [selectedStartDate, setSelectedStartDate] = useState<Dayjs>(dayjs().subtract(30, 'days'));
  const [selectedEndDate, setSelectedEndDate] = useState<Dayjs>(dayjs());

  // Fetch dashboard data
  const { data: dashboardData, error: dashboardError } = useQuery(
    ['dashboard', dateRange, selectedCategory],
    () => api.get('/api/reports/dashboard', {
      params: {
        start_date: dateRange[0].format('YYYY-MM-DD'),
        end_date: dateRange[1].format('YYYY-MM-DD'),
        category: selectedCategory
      }
    }).then(res => res.data),
    {
      staleTime: 5 * 60 * 1000,
      retry: 3,
      onError: (error) => {
        console.error('Dashboard error:', error);
      }
    }
  );

  const { data: lowStockData } = useQuery('low-stock', () =>
    api.get('/api/inventory/low-stock').then(res => res.data),
    { 
      staleTime: 5 * 60 * 1000, 
      retry: 3,
      onError: (error) => {
        console.error('Low stock error:', error);
      }
    }
  );

  const { data: expiringData } = useQuery('expiring', () =>
    api.get('/api/inventory/expiring').then(res => res.data),
    { 
      staleTime: 5 * 60 * 1000, 
      retry: 3,
      onError: (error) => {
        console.error('Expiring data error:', error);
      }
    }
  );

  const { data: categoriesData } = useQuery('dashboard-categories', () =>
    api.get('/api/master-data/categories').then(res => res.data),
    { 
      staleTime: 5 * 60 * 1000, 
      retry: 3,
      onError: (error) => {
        console.error('Categories error:', error);
      }
    }
  );

  // Handle date selection
  const handleStartDateSelect = (date: Dayjs) => {
    setSelectedStartDate(date);
    setDateRange([date, dateRange[1]]);
  };

  const handleEndDateSelect = (date: Dayjs) => {
    setSelectedEndDate(date);
    setDateRange([dateRange[0], date]);
  };

  // Statistics cards data
  const statistics = [
    {
      title: 'Tổng số thuốc',
      value: dashboardData?.data?.total_medicines || 0,
      icon: <MedicineBoxOutlined />,
      color: '#1890ff',
      trend: '+12%',
      trendUp: true,
      suffix: 'loại',
    },
    {
      title: 'Tổng lô thuốc',
      value: dashboardData?.data?.total_lots || 0,
      icon: <ShoppingCartOutlined />,
      color: '#52c41a',
      trend: '+8%',
      trendUp: true,
      suffix: 'lô',
    },
    {
      title: 'Tồn kho thấp',
      value: dashboardData?.data?.low_stock_count || lowStockData?.data?.length || 0,
      icon: <ArrowDownOutlined />,
      color: '#faad14',
      trend: '-5%',
      trendUp: false,
      suffix: 'loại',
      onClick: () => setLowStockVisible(true)
    },
    {
      title: 'Sắp hết hạn',
      value: dashboardData?.data?.expiring_count || expiringData?.data?.length || 0,
      icon: <ArrowUpOutlined />,
      color: '#ff4d4f',
      trend: '+3%',
      trendUp: true,
      suffix: 'lô',
      onClick: () => setExpiringVisible(true)
    }
  ];

  // Error state
  if (dashboardError) {
    return (
      <div className={styles.container}>
        <Alert 
          message="Lỗi tải dữ liệu"
          description="Không thể tải dữ liệu dashboard. Vui lòng thử lại."
          type="error"
          action={
            <Button size="small" onClick={() => window.location.reload()}>
              Tải lại
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className={styles.container}>

      {/* Filter Section */}
      <Card 
        className={styles.filterCard}
        style={{ 
          marginBottom: 16, 
          background: '#f8f9fa', 
          border: '1px solid #e9ecef',
          borderRadius: 8
        }}
      >
        <div 
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 8, 
            cursor: 'pointer',
            padding: '8px 0'
          }}
          onClick={() => setFilterExpanded(!filterExpanded)}
        >
          <FilterOutlined style={{ color: '#666' }} />
          <Text strong style={{ fontSize: 14, flex: 1 }}>Bộ lọc</Text>
          <Text style={{ color: '#666', fontSize: 12 }}>
            {selectedStartDate.format('DD/MM')} - {selectedEndDate.format('DD/MM')}
            {selectedCategory !== 'all' && ` | ${categoriesData?.data?.find((cat: any) => cat.id === selectedCategory)?.name || ''}`}
          </Text>
          <div style={{ 
            transform: filterExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease',
            fontSize: 12,
            color: '#666'
          }}>
            ▼
          </div>
        </div>
        
        {filterExpanded && (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: 12,
            paddingTop: 12,
            borderTop: '1px solid #e9ecef'
          }}>
            {/* Date Range Filter */}
            <div>
              <Text style={{ fontSize: 13, color: '#666', marginBottom: 4, display: 'block' }}>
                Khoảng thời gian:
              </Text>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button
                  onClick={() => setStartDateModalOpen(true)}
                  style={{ 
                    background: '#fff', 
                    border: '1px solid #d9d9d9', 
                    borderRadius: '6px', 
                    padding: '8px 12px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    flex: 1,
                    color: '#333',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  {selectedStartDate.format('DD/MM/YYYY')}
                </button>
                <span style={{ color: '#666' }}>-</span>
                <button
                  onClick={() => setEndDateModalOpen(true)}
                  style={{ 
                    background: '#fff', 
                    border: '1px solid #d9d9d9', 
                    borderRadius: '6px', 
                    padding: '8px 12px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    flex: 1,
                    color: '#333',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  {selectedEndDate.format('DD/MM/YYYY')}
                </button>
              </div>
            </div>

            {/* Category Filter */}
            <div>
              <Text style={{ fontSize: 13, color: '#666', marginBottom: 4, display: 'block' }}>
                Danh mục:
              </Text>
              <Select
                value={selectedCategory}
                onChange={setSelectedCategory}
                style={{ width: '100%' }}
                placeholder="Chọn danh mục"
                size="middle"
              >
                <Option value="all">Tất cả</Option>
                {categoriesData?.data?.map((cat: any) => (
                  <Option key={cat.id} value={cat.id}>{cat.name}</Option>
                ))}
              </Select>
            </div>
          </div>
        )}
      </Card>

      {/* Statistics Cards */}
      <div className={styles.statsContainer}>
        <Row gutter={[8, 8]}>
          {statistics.map((stat, index) => (
            <Col span={isMobile ? 12 : 6} key={index}>
              <Card 
                className={styles.statCard}
                onClick={stat.onClick}
                style={{ cursor: stat.onClick ? 'pointer' : 'default' }}
              >
                <div className={styles.statContent}>
                  <div className={styles.statIcon} style={{ color: stat.color }}>
                    {stat.icon}
                  </div>
                  <div className={styles.statInfo}>
                    <div className={styles.statValue}>
                      {stat.value}
                      <span className={styles.statSuffix}>{stat.suffix}</span>
                    </div>
                    <div className={styles.statTitle}>{stat.title}</div>
                    <div className={styles.statTrend}>
                      <Text 
                        type={stat.trendUp ? 'success' : 'danger'}
                        style={{ fontSize: 10 }}
                      >
                        {stat.trendUp ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                        {' '}{stat.trend}
                      </Text>
                    </div>
                  </div>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      </div>

      {/* Quick Info Cards */}
      <div className={styles.quickInfo}>
        <Card 
          className={styles.infoCard}
          onClick={() => setLowStockVisible(true)}
        >
          <div className={styles.infoHeader}>
            <ArrowDownOutlined style={{ color: '#faad14' }} />
            <Text strong>Tồn kho thấp</Text>
            <Badge count={lowStockData?.data?.length || 0} />
          </div>
          <div className={styles.infoContent}>
            {(lowStockData?.data || []).slice(0, 3).map((item: any, index: number) => (
              <div key={index} className={styles.infoItem}>
                <Text ellipsis style={{ flex: 1, textAlign: 'left' }}>{item.name}</Text>
                <Text type="danger" style={{ fontSize: 12 }}>
                  {item.current_stock} {item.unit}
                </Text>
              </div>
            ))}
          </div>
        </Card>

        <Card 
          className={styles.infoCard}
          onClick={() => setExpiringVisible(true)}
        >
          <div className={styles.infoHeader}>
            <ArrowUpOutlined style={{ color: '#ff4d4f' }} />
            <Text strong>Sắp hết hạn</Text>
            <Badge count={expiringData?.data?.length || 0} />
          </div>
          <div className={styles.infoContent}>
            {(expiringData?.data || []).slice(0, 3).map((item: any, index: number) => (
              <div key={index} className={styles.infoItem}>
                <Text ellipsis style={{ flex: 1, textAlign: 'left' }}>{item.medicine_name}</Text>
                <Text type="danger" style={{ fontSize: 12 }}>
                  {item.days_until_expiry} ngày
                </Text>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Low Stock Detail Modal */}
      <Modal
        title="Chi tiết tồn kho thấp"
        open={lowStockVisible}
        onCancel={() => setLowStockVisible(false)}
        footer={null}
        width="90%"
      >
        <List
          dataSource={lowStockData?.data || []}
          renderItem={(item: any) => (
            <List.Item>
              <List.Item.Meta
                title={item.name}
                description={`Mã: ${item.code} | Tồn kho: ${item.current_stock} ${item.unit}`}
              />
              <div>
                <Progress 
                  percent={Math.round((item.current_stock / item.min_stock_level) * 100)} 
                  size="small" 
                  status={item.current_stock < item.min_stock_level ? 'exception' : 'normal'}
                />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Tối thiểu: {item.min_stock_level} {item.unit}
                </Text>
              </div>
            </List.Item>
          )}
        />
      </Modal>

      {/* Expiring Detail Modal */}
      <Modal
        title="Chi tiết thuốc sắp hết hạn"
        open={expiringVisible}
        onCancel={() => setExpiringVisible(false)}
        footer={null}
        width="90%"
      >
        <List
          dataSource={expiringData?.data || []}
          renderItem={(item: any) => (
            <List.Item>
              <List.Item.Meta
                title={item.medicine_name}
                description={`Lô: ${item.lot_number} | Số lượng: ${item.quantity} ${item.unit}`}
              />
              <div style={{ textAlign: 'right' }}>
                <div style={{ marginBottom: 4 }}>
                  <Text>{dayjs(item.expiry_date).format('DD/MM/YYYY')}</Text>
                </div>
                <Tag color={item.days_until_expiry <= 30 ? 'red' : item.days_until_expiry <= 90 ? 'orange' : 'green'}>
                  {item.days_until_expiry} ngày
                </Tag>
              </div>
            </List.Item>
          )}
        />
      </Modal>

      {/* Date Picker Modals */}
      <DatePickerModal
        isOpen={startDateModalOpen}
        onClose={() => setStartDateModalOpen(false)}
        onSelect={handleStartDateSelect}
        selectedDate={selectedStartDate}
        title="Chọn ngày bắt đầu"
      />
      <DatePickerModal
        isOpen={endDateModalOpen}
        onClose={() => setEndDateModalOpen(false)}
        onSelect={handleEndDateSelect}
        selectedDate={selectedEndDate}
        title="Chọn ngày kết thúc"
      />
    </div>
  );
};

export default DashboardPageMobile;
